import type { DocumentData, DocumentSnapshot, QueryDocumentSnapshot } from "firebase/firestore";
import { Timestamp } from "firebase/firestore";

/**
 * The read boundary: one place where raw Firestore data becomes data the rest
 * of the app can trust.
 *
 * Firestore returns three different shapes for the same logical timestamp — a
 * real `Timestamp` (client-SDK write), a `{seconds,nanoseconds}` map, or a
 * `{_seconds,_nanoseconds}` map (mobile writes and admin-SDK/export
 * round-trips). Documents then reach components through unvalidated
 * `{ id, ...data } as Trial` casts, so every consumer believes it holds a
 * `Timestamp` and calls `.toDate()`. On the two plain-map shapes that throws,
 * and with no error boundary in the app a throw in a list unmounts the route.
 *
 * Normalizing here makes the cast's promise true instead of aspirational, and
 * has a second effect that guards cannot: edit forms that spread a loaded
 * document back into an update (`{...task}`) re-persist the *normalized* value,
 * so a malformed document heals the next time anyone saves it.
 *
 * What it deliberately does NOT do is invent data. A field that is missing stays
 * missing, and a timestamp map that cannot produce a valid `Timestamp` is left
 * exactly as found — callers still need `timestampToDate`/`formatTimestampDate`
 * from `@/lib/utils` for those.
 */

/** Recursion limit. Firestore data is acyclic; this only bounds hand-built input. */
const MAX_DEPTH = 64;

/**
 * Only plain objects and arrays are walked. Anything with its own prototype —
 * `Timestamp`, `GeoPoint`, `DocumentReference`, `Bytes`, `Date` — is returned
 * untouched, because rebuilding one as a plain object would strip its methods
 * and break every reference-based relationship in the schema.
 */
const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (typeof value !== "object" || value === null) return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

/**
 * The real `Timestamp` a plain object encodes, or `undefined` if it does not
 * encode one.
 *
 * The key set must match EXACTLY ­— hence taking `keys` from the caller, which
 * has already computed it. A map that merely *contains* `seconds` and
 * `nanoseconds` alongside other fields is some other payload, and converting it
 * would silently discard those other fields.
 */
const toTimestamp = (
  value: Record<string, unknown>,
  keys: string[]
): Timestamp | undefined => {
  if (keys.length !== 2) return undefined;

  const plain = keys.includes("seconds") && keys.includes("nanoseconds");
  if (!plain && !(keys.includes("_seconds") && keys.includes("_nanoseconds"))) return undefined;

  const seconds = value[plain ? "seconds" : "_seconds"];
  const nanoseconds = value[plain ? "nanoseconds" : "_nanoseconds"];
  if (typeof seconds !== "number" || typeof nanoseconds !== "number") return undefined;

  // `new Timestamp(NaN, 0)` does NOT throw — Firestore's range check is a pair
  // of comparisons, and every comparison against NaN is false. Check explicitly.
  if (!Number.isFinite(seconds) || !Number.isFinite(nanoseconds)) return undefined;

  try {
    return new Timestamp(seconds, nanoseconds); // out-of-range throws; that IS the range check
  } catch {
    return undefined;
  }
};

/**
 * Module-scope so a snapshot of N documents allocates one closure, not N.
 *
 * Copies lazily: nothing is allocated until a descendant actually changes, at
 * which point the container is shallow-copied and patched. Primitives are
 * skipped outright — a `number[]` or `string[]` can never contain a timestamp,
 * and mapping over one only to throw the copy away was the single most wasteful
 * thing this function used to do.
 */
const walk = (value: unknown, depth: number): unknown => {
  if (typeof value !== "object" || value === null) return value;
  if (depth > MAX_DEPTH) return value;

  if (Array.isArray(value)) {
    let next: unknown[] | undefined;
    for (let i = 0; i < value.length; i++) {
      const item = value[i];
      if (typeof item !== "object" || item === null) continue;
      const walked = walk(item, depth + 1);
      if (walked !== item) {
        if (!next) next = value.slice();
        next[i] = walked;
      }
    }
    return next ?? value;
  }

  if (!isPlainObject(value)) return value;

  const keys = Object.keys(value);
  const timestamp = toTimestamp(value, keys);
  if (timestamp) return timestamp;

  let next: Record<string, unknown> | undefined;
  for (const key of keys) {
    const item = value[key];
    if (typeof item !== "object" || item === null) continue;
    const walked = walk(item, depth + 1);
    if (walked !== item) {
      if (!next) next = { ...value };
      next[key] = walked;
    }
  }
  return next ?? value;
};

/**
 * Replaces serialized timestamp maps with real `Timestamp` instances, returning
 * the ORIGINAL reference for any value — at any depth — that did not need it.
 *
 * A healthy document therefore costs a traversal and no allocation at all.
 *
 * Call it once per snapshot, inside the `onSnapshot` handler — never in a render
 * body, where it would re-run on every render rather than every update.
 */
export const normalizeFirestoreData = <T>(data: T): T => walk(data, 0) as T;

/**
 * Turns a Firestore snapshot into a domain object: the document id plus its
 * normalized data.
 *
 * This replaces the `{ id: doc.id, ...doc.data() } as T` literal that was
 * hand-written at every read site. Key order is identical to what it replaces —
 * the id first, data spread over it — so a document that stores its own `id`
 * field still wins, exactly as before.
 *
 * @param idField the property to attach the document id to. Plots are keyed by
 *   `plotID` rather than `id`.
 * @param extra additional fields to place alongside the id, for the collection-
 *   group reads that need `doc.ref` to know which organization a result came
 *   from. Stored data still takes precedence, matching the literal.
 */
export const fromFirestoreDoc = <T>(
  snap: DocumentSnapshot<DocumentData> | QueryDocumentSnapshot<DocumentData>,
  idField = "id",
  extra?: Record<string, unknown>
): T =>
  ({
    [idField]: snap.id,
    ...extra,
    // `data()` is undefined for a snapshot of a document that does not exist.
    ...normalizeFirestoreData(snap.data()),
  }) as T;
